import { HTMLAttributes, useEffect, useRef, useState } from 'react';

import DropdownArrow from '../../../images/dropdown-arrow.svg';
import Check from '../../../images/check.svg';
import './styles.css';

interface Option<T> {
  label: string;
  value: T;
}

interface CustomSelectProps<T>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  onChange?: (option: Option<T>) => void;
  optionAttrs?: HTMLAttributes<HTMLDivElement>;
  options: Option<T>[];
  value?: Option<T>['value'];
}

const isHTMLElement = (t: EventTarget): t is HTMLElement => {
  return t && t instanceof HTMLElement;
};

export default function CustomSelect<T>(
  props: CustomSelectProps<T>
): JSX.Element {
  const { onChange, optionAttrs, options, value, ...divProps } = props;
  const thisNode = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState(
    value && options.length ? options.find((o) => o.value === value) : undefined
  );

  const getClass = (option: Option<T>, selected?: Option<T> | undefined) => {
    let optionClass = 'option';
    if (selected && selected.value === option.value) {
      optionClass += ' selected';
    }
    return optionClass;
  };

  const clickHandler: EventListener = (e) => {
    if (e.target && isHTMLElement(e.target) && thisNode.current) {
      if (thisNode.current.contains(e.target)) {
        setOpened(!opened);
      } else {
        if (opened) {
          setOpened(!opened);
        }
      }
    }
  };

  const selectOption = (option: Option<T>) => () => {
    if (!selected || selected.value !== option.value) {
      setSelected(option);
      if (onChange) {
        onChange(option);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  return (
    <div
      className={`custom-select${opened ? ' opened' : ''}`}
      {...divProps}
      ref={thisNode}
    >
      <div className="value">
        <span className="text">
          {typeof selected === 'undefined' ? 'Select...' : selected.label}
        </span>
        <span className="arrow">
          <img src={DropdownArrow} />
        </span>
      </div>
      <div className="options">
        {options.map((option, index) => (
          <div
            {...optionAttrs}
            className={getClass(option, selected)}
            data-value={option.value}
            key={index}
            onClick={selectOption(option)}
          >
            {option.label}
            {selected && selected.value === option.value ? (
              <img className="check" src={Check} />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
